import React from 'react'
import { cn } from '@/lib/utils'

interface Reveal3Props {
  children: React.ReactNode
  /** transition-delay in ms — for staggering siblings */
  delay?: number
  className?: string
  as?: React.ElementType
  id?: string
}

/**
 * Landing3 scroll-reveal (same contract as marketing2/Reveal2, mkt3-scoped CSS
 * so the variant never depends on v1/v2's stylesheets being in the bundle).
 * prefers-reduced-motion renders the static final state via marketing3.css.
 * Environments without IntersectionObserver (jsdom) render immediately visible.
 */
export default function Reveal3({ children, delay = 0, className, as: Tag = 'div', id }: Reveal3Props) {
  const ref = React.useRef<HTMLElement | null>(null)
  const [inView, setInView] = React.useState(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true)
          io.disconnect()
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <Tag
      ref={ref as React.Ref<never>}
      id={id}
      className={cn('mkt3-reveal', inView && 'mkt3-in', className)}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  )
}
